import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Calendar, Clock, BookOpen, User, Target } from "lucide-react";
import { format } from "date-fns";
import { useParentData } from "@/hooks/useParentData";
import Seo from "@/components/seo/Seo";

const formatTime = (time: string) => {
  const [h, m] = time.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:${m} ${ampm}`;
};

export default function ParentDashboard() {
  const { children, schedules, instructorProfiles, testInvitations, loading } = useParentData();

  const todayKey = new Date().toISOString().split("T")[0];
  const todays = schedules.filter((s) => s.session_date === todayKey && s.status === "scheduled");
  const upcoming = schedules.filter((s) => s.status === "scheduled").length;
  const pendingTests = testInvitations.filter((t) => !t.completed_at).length;

  const getInstructorName = (id: string) => {
    const p = instructorProfiles[id];
    return p ? `${p.first_name} ${p.last_name}`.trim() : "Instructor";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const kpis = [
    { label: "Children", value: children.length, icon: Users, to: "/parent/children" },
    { label: "Upcoming sessions", value: upcoming, icon: Calendar, to: "/parent/calendar" },
    { label: "Active students", value: new Set(schedules.map((s) => s.student_email)).size, icon: BookOpen, to: "/parent/children" },
    { label: "Pending tests", value: pendingTests, icon: Target, to: "/parent/tests" },
  ];

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <Seo title="Parent Dashboard" description="Today at a glance for your family." canonicalPath="/parent" noindex />
      <header>
        <h1 className="text-3xl font-bold">Parent Dashboard</h1>
        <p className="text-muted-foreground">Today at a glance for your family.</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Link key={k.label} to={k.to}>
            <Card className="hover:bg-accent/50 transition-colors h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{k.label}</CardTitle>
                <k.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{k.value}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Today's sessions</CardTitle>
            <CardDescription>Tutoring scheduled for today across your children</CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/parent/calendar">Open calendar</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {todays.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-10 w-10 mx-auto mb-2" />
              No sessions scheduled today.
            </div>
          ) : (
            <div className="space-y-3">
              {todays.map((s) => (
                <div key={s.id} className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium">{s.student_name}</p>
                      <p className="text-xs text-muted-foreground">{s.subject}{s.topic ? ` · ${s.topic}` : ""}</p>
                    </div>
                    <Badge variant="default">{s.status}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><User className="h-4 w-4" />{getInstructorName(s.instructor_id)}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{format(new Date(s.session_date), "EEE, MMM dd")}</span>
                    <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {children.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">You haven't added any children yet.</p>
            <Button asChild>
              <Link to="/parent/children">Add your first child</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
